import { internalApiBaseUrl, Service } from "@client/http/client";
import { AssetSource, AssetType, ILeanAsset } from "@nendaTypes/NendaTypes";
import React, { useEffect, useState } from "react";
import ShakaPlayer from "../../ShakaPlayer";
import {
  Dialog,
  DialogContent,
  IconButton,
  Box,
  CircularProgress,
  Typography,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useWindowSize } from "@client/hooks/useWindowSize";

export function isPreviewableAsset(asset: ILeanAsset): boolean {
  return asset.source === AssetSource.REDBEE && asset.type === AssetType.VIDEO;
}

interface PlayResponse {
  formats?: { format: string; mediaLocator: string }[];
}

interface AssetPreviewDialogProps {
  isOpen: boolean;
  assetId?: string;
  title?: string;
  unavailableReason?: string;
  onClose: () => void;
  onError?: (error: Error) => void;
}

const AssetPreviewDialog: React.FC<AssetPreviewDialogProps> = ({
  isOpen,
  assetId,
  title,
  unavailableReason,
  onClose,
  onError,
}) => {
  const [resolvedSrc, setResolvedSrc] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { w: vw, h: vh } = useWindowSize();

  useEffect(() => {
    if (!isOpen) return;
    const resolveAsset = async (id: string, signal: AbortSignal) => {
      setLoading(true);
      setError(null);
      try {
        const service = new Service();

        const response = await fetch(
          `${internalApiBaseUrl}/playback/preview/${encodeURIComponent(id)}`,
          {
            method: "POST",
            headers: {
              ...service.authHeader(),
            },
            signal,
          }
        );

        if (!response.ok)
          throw new Error(`Failed to resolve asset (${response.status})`);

        const resolvedAsset: PlayResponse = await response.json();
        const dashURI = resolvedAsset.formats?.find(
          (x) => x.format === "DASH"
        )?.mediaLocator;

        if (!dashURI) throw new Error("No DASH (.mpd) URL in response");

        setResolvedSrc(dashURI);
      } catch (e: any) {
        if (e?.name === "AbortError") return;
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err.message);
        onError?.(err);
      } finally {
        setLoading(false);
      }
    };

    if (!assetId) {
      const message = unavailableReason ?? "Nothing to preview";
      setError(message);
      onError?.(new Error(message));
      return;
    }

    const ac = new AbortController();
    requestAnimationFrame(() => resolveAsset(assetId, ac.signal));

    return () => {
      ac.abort();
      setResolvedSrc(undefined);
    };
  }, [assetId, isOpen]);

  const handleClose = () => {
    setResolvedSrc(undefined);
    setError(null);
    onClose();
  };

  const maxW = vw * 0.9;
  const maxH = vh * 0.9;

  let width = "auto";
  let height = "auto";

  if ((maxW * 9) / 16 <= maxH) {
    width = `${maxW}px`;
  } else {
    height = `${maxH}px`;
  }

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      keepMounted
      maxWidth={false}
      fullWidth={false}
      sx={{
        "& .MuiDialog-paper": {
          aspectRatio: "16 / 9",
          width,
          height,
          maxWidth: `${maxW}px`,
          maxHeight: `${maxH}px`,
          margin: 0,
          display: "block",
          position: "relative",
          overflow: "hidden",
          borderRadius: 0,
        },
      }}
    >
      <DialogContent
        sx={{
          p: 0,
          position: "relative",
        }}
      >
        <Box
          sx={{
            position: "relative",
            width: "100%",
            maxWidth: "100%",
            maxHeight: "100%",
            height: "100%",
            aspectRatio: "16 / 9",
            bgcolor: "white",
          }}
        >
          {loading && !resolvedSrc && (
            <Box
              sx={{
                position: "absolute",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                inset: 0,
                placeItems: "center",
              }}
            >
              <CircularProgress size={28} />
            </Box>
          )}

          {!loading && error && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "grid",
                placeItems: "center",
                p: 2,
              }}
            >
              <Alert color="error">Failed to preview content</Alert>
            </Box>
          )}

          {resolvedSrc && (
            <ShakaPlayer
              controls={false}
              showSubtitles
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                background: "black",
                objectFit: "contain",
              }}
              src={resolvedSrc}
              autoPlay
            />
          )}

          <IconButton
            aria-label="Close"
            onClick={handleClose}
            sx={{
              position: "absolute",
              right: 18,
              top: 8,
              zIndex: 1,
              bgcolor: "rgba(255,255,255,0.9)",
              "&:hover": { bgcolor: "rgba(255,255,255,1)" },
            }}
          >
            <CloseIcon />
          </IconButton>

          {title && (
            <Typography
              variant="caption"
              sx={{
                position: "absolute",
                left: 22,
                top: 12,
                px: 1,
                py: 0.25,
                zIndex: 1,
                borderRadius: 1,
                color: "common.white",
                bgcolor: "rgba(0,0,0,0.55)",
              }}
            >
              {title}
            </Typography>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default AssetPreviewDialog;
